import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  User, Building, Clock, CheckCircle2, MapPin, Activity, 
  Calendar, Bed, Camera, RefreshCw, Phone, MessageSquare,
  Image, ChevronRight, AlertTriangle, Heart, Users
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const FacilityTransparencyPortal = () => {
  const [dashboard, setDashboard] = useState(null);
  const [visits, setVisits] = useState([]);
  const [woundTracker, setWoundTracker] = useState(null);
  const [bedHoldAlerts, setBedHoldAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('schedule');
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  const fetchDashboard = useCallback(async () => {
    try {
      // Simulated fetch - in production this would be from the facility's patient list
      const res = await fetch(`${API}/api/phase38-44/facility/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
        setVisits(data.upcoming_visits || []);
        setBedHoldAlerts(data.bed_hold_alerts || []);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboard();
    // Poll for live ETAs
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  // Acknowledge bed-hold alert
  const acknowledgeBedHold = async (alertId) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/facility/bed-hold/${alertId}/acknowledge`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast({ title: "Alert Acknowledged", description: "Bed status has been updated." });
        fetchDashboard();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to acknowledge alert", variant: "destructive" });
    }
  };

  // Request bed hold
  const requestBedHold = async (patientId, days, reason) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/facility/request-bed-hold`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ patient_id: patientId, days, reason })
      });
      if (res.ok) {
        toast({ title: "Bed Hold Requested", description: `${days}-day hold has been requested.` });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to request bed hold", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Building className="h-7 w-7 text-teal-600" />
            Facility Continuity Dashboard
          </h1>
          <p className="text-gray-600">Real-time visibility into hospice team activities</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {dashboard?.patients_count || 0} Patients in Facility
        </Badge>
      </div>

      {/* Urgent Bed-Hold Alerts */}
      {bedHoldAlerts.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-orange-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Bed-Hold Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {bedHoldAlerts.map((alert, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <h4 className="font-medium">{alert.patient_name}</h4>
                    <p className="text-sm text-gray-600">{alert.message}</p>
                    <p className="text-xs text-gray-500">
                      {alert.type === 'transfer_out' ? 'Transfer Out' : 'Return Expected'}: {alert.date}
                    </p>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={() => acknowledgeBedHold(alert.id)}
                  >
                    Acknowledge
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="schedule">IDT Schedule</TabsTrigger>
          <TabsTrigger value="wounds">Wound Tracker</TabsTrigger>
          <TabsTrigger value="transitions">Transitions</TabsTrigger>
        </TabsList>

        {/* IDT Schedule with Live ETA */}
        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-teal-600" />
                Upcoming Hospice Visits
              </CardTitle>
              <CardDescription>Live ETAs for scheduled IDT members</CardDescription>
            </CardHeader>
            <CardContent>
              {visits.length > 0 ? (
                <div className="space-y-3">
                  {visits.map((visit, i) => (
                    <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full ${
                          visit.status === 'in_transit' ? 'bg-green-100' :
                          visit.status === 'confirmed' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <User className={`h-5 w-5 ${
                            visit.status === 'in_transit' ? 'text-green-600' :
                            visit.status === 'confirmed' ? 'text-blue-600' : 'text-gray-600'
                          }`} />
                        </div>
                        <div>
                          <h4 className="font-medium">{visit.staff_name}</h4>
                          <p className="text-sm text-gray-600">{visit.role} - {visit.patient_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-500">
                              Scheduled: {visit.scheduled_time}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {visit.status === 'in_transit' && visit.eta_minutes && (
                          <div className="mb-2">
                            <Badge className="bg-green-500 animate-pulse">
                              <MapPin className="h-3 w-3 mr-1" />
                              ETA: {visit.eta_minutes} min
                            </Badge>
                          </div>
                        )}
                        <Badge variant="outline">
                          {visit.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p>No upcoming visits scheduled</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Today's Visit Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Today's Visit Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-teal-600">
                    {dashboard?.today_summary?.completed || 0}
                  </p>
                  <p className="text-sm text-gray-600">Completed</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">
                    {dashboard?.today_summary?.scheduled || 0}
                  </p>
                  <p className="text-sm text-gray-600">Scheduled</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">
                    {dashboard?.today_summary?.in_progress || 0}
                  </p>
                  <p className="text-sm text-gray-600">In Progress</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-orange-600">
                    {dashboard?.today_summary?.rescheduled || 0}
                  </p>
                  <p className="text-sm text-gray-600">Rescheduled</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shared Wound Care Tracker */}
        <TabsContent value="wounds" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Image className="h-5 w-5 text-teal-600" />
                Shared Wound Care Tracker
              </CardTitle>
              <CardDescription>
                Collaborative wound documentation between facility and hospice
              </CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.wound_cases?.length > 0 ? (
                <div className="space-y-4">
                  {dashboard.wound_cases.map((wound, i) => (
                    <div key={i} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium">{wound.patient_name}</h4>
                          <p className="text-sm text-gray-600">
                            {wound.location} - {wound.type}
                          </p>
                        </div>
                        <Badge className={
                          wound.status === 'improving' ? 'bg-green-500' :
                          wound.status === 'stable' ? 'bg-blue-500' :
                          wound.status === 'worsening' ? 'bg-red-500' : 'bg-gray-500'
                        }>
                          {wound.status}
                        </Badge>
                      </div>

                      {/* Healing Timeline */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                          <span>Healing Progress</span>
                          <span>{wound.healing_percentage || 0}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full">
                          <div 
                            className={`h-2 rounded-full ${
                              wound.status === 'improving' ? 'bg-green-500' :
                              wound.status === 'stable' ? 'bg-blue-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${wound.healing_percentage || 0}%` }}
                          />
                        </div>
                      </div>

                      {/* Photo Timeline */}
                      <div className="flex gap-2 overflow-x-auto py-2">
                        {wound.photos?.slice(-4).map((photo, j) => (
                          <div key={j} className="flex-shrink-0 relative">
                            <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center border">
                              <Camera className="h-6 w-6 text-gray-400" />
                            </div>
                            <span className="absolute bottom-1 left-1 text-[10px] bg-black/50 text-white px-1 rounded">
                              {photo.date?.split('T')[0]}
                            </span>
                          </div>
                        ))}
                        <div className="flex-shrink-0 w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-teal-400">
                          <Camera className="h-6 w-6 text-gray-400" />
                        </div>
                      </div>

                      <div className="mt-3 flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1">
                          View Full Timeline
                        </Button>
                        <Button size="sm" className="flex-1 bg-teal-600 hover:bg-teal-700">
                          Add Photo
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Image className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p>No active wound cases</p>
                  <p className="text-sm">Wound documentation will appear here when available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transitions / Bed-Hold Management */}
        <TabsContent value="transitions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bed className="h-5 w-5 text-teal-600" />
                Bed-Hold Management
              </CardTitle>
              <CardDescription>
                Coordinate patient transitions without phone tag
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Active Holds */}
                {dashboard?.active_bed_holds?.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-700">Active Bed Holds</h4>
                    {dashboard.active_bed_holds.map((hold, i) => (
                      <div key={i} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">{hold.patient_name}</h4>
                            <p className="text-sm text-gray-600">Room {hold.room_number}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline">{hold.reason}</Badge>
                              <span className="text-xs text-gray-500">
                                Hold Until: {hold.hold_until}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-blue-600">
                              {hold.days_remaining}
                            </p>
                            <p className="text-xs text-gray-500">days remaining</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <Bed className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                    <p>No active bed holds</p>
                  </div>
                )}

                {/* Request New Hold */}
                <Card className="bg-gray-50">
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-3">Request New Bed Hold</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input placeholder="Patient Name" />
                      <Input type="number" placeholder="Days (3-14)" min="3" max="14" />
                      <Button className="bg-teal-600 hover:bg-teal-700">
                        Request Hold
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Transitions */}
                <div className="mt-6">
                  <h4 className="font-medium text-gray-700 mb-3">Recent Transitions</h4>
                  <div className="space-y-2">
                    {dashboard?.recent_transitions?.map((trans, i) => (
                      <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          {trans.type === 'return' ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-blue-500" />
                          )}
                          <div>
                            <p className="font-medium">{trans.patient_name}</p>
                            <p className="text-sm text-gray-600">{trans.description}</p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-500">{trans.date}</span>
                      </div>
                    )) || (
                      <p className="text-center text-gray-500 py-4">No recent transitions</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FacilityTransparencyPortal;
